import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// Format currency in Nigerian Naira
export const formatCurrency = (amount, currency = "NGN", showSymbol = true) => {
  const formatted = new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
  
  if (showSymbol) {
    return currency === "NGN" ? `₦${formatted}` : `${currency} ${formatted}`;
  }
  return formatted;
};

// Format date
export const formatDate = (dateString, options = {}) => {
  const date = new Date(dateString);
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };
  return new Intl.DateTimeFormat('en-NG', defaultOptions).format(date);
};

// Format time
export const formatTime = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-NG', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Format relative time
export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  
  return formatDate(dateString);
};

// Get greeting based on time
export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

// Mask account number
export const maskAccountNumber = (account) => {
  if (!account || account.length <= 4) return account;
  return '*'.repeat(account.length - 4) + account.slice(-4);
};

// Mask phone number
export const maskPhone = (phone) => {
  if (!phone || phone.length <= 4) return phone;
  return phone.slice(0, 4) + '*'.repeat(phone.length - 8) + phone.slice(-4);
};

// Get initials from name
export const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Generate random color for avatar
export const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];
  
  let hash = 0;
  for (let i = 0; i < name?.length || 0; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

// Copy to clipboard
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
};

// Validate Nigerian phone number
export const isValidNigerianPhone = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  return /^(234|0)(70|80|81|90|91|70|71)\d{8}$/.test(cleaned);
};

// Format phone number
export const formatPhone = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('234')) {
    return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
  }
  if (cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  return phone;
};

// Validate email
export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Transaction type to icon and color
export const getTransactionStyle = (type, direction) => {
  if (direction === 'in') {
    return {
      icon: 'ArrowDownLeft',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    };
  }
  return {
    icon: 'ArrowUpRight',
    color: 'text-white',
    bgColor: 'bg-white/10',
  };
};

// Status badge styles
export const getStatusStyle = (status) => {
  const styles = {
    completed: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Completed' },
    pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', label: 'Pending' },
    processing: { bg: 'bg-blue-500/10', text: 'text-blue-500', label: 'Processing' },
    failed: { bg: 'bg-red-500/10', text: 'text-red-500', label: 'Failed' },
    active: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Active' },
    paused: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', label: 'Paused' },
    draft: { bg: 'bg-gray-500/10', text: 'text-gray-500', label: 'Draft' },
    approved: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Approved' },
    rejected: { bg: 'bg-red-500/10', text: 'text-red-500', label: 'Rejected' },
  };
  
  return styles[status] || styles.pending;
};

// KYC tier info
export const getKYCTierInfo = (tier) => {
  const tiers = {
    1: {
      name: 'Basic',
      color: 'text-gray-400',
      bgColor: 'bg-gray-500/10',
      limits: { daily: 50000, single: 20000, monthly: 300000 },
      features: ['View balance', 'Receive money', 'Limited transfers'],
    },
    2: {
      name: 'Standard',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      limits: { daily: 500000, single: 200000, monthly: 3000000 },
      features: ['All Basic features', 'Bank withdrawals', 'Higher limits'],
    },
    3: {
      name: 'Premium',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      limits: { daily: 5000000, single: 2000000, monthly: 50000000 },
      features: ['All Standard features', 'Unlimited transfers', 'Priority support'],
    },
  };
  
  return tiers[tier] || tiers[1];
};
